import { ConsultantReferenceDocument, Document } from "@bonliva-crm/graphql";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  makeStyles
} from "@material-ui/core";
import * as React from "react";
import { CheckedIcon, DraftIcon } from "../../icons";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import FolderIcon from "../../icons/components/folder-icon";
import { Color } from "../../theme";

type DocumentSlice = Pick<ConsultantReferenceDocument, "referenceId" | "name"> & {
  document: Pick<Document, "PreviewURL">;
};

type Props = {
  label?: string;
  groupingKey: string;
  documents: DocumentSlice[];
  value: DocumentSlice[];
  onChange: (values: DocumentSlice[]) => void;
};

export const ConsultantReferenceDocumentList = React.forwardRef<HTMLDivElement, Props>(
  ({ label, groupingKey, documents, value, onChange }, ref) => {
    const classes = useStyles();

    const isAllChecked = documents.every((doc) =>
      value.some((selected) => selected.referenceId === doc.referenceId)
    );

    const handleGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        onChange([...value, ...documents]);
      } else {
        onChange(
          value.filter(
            (selected) =>
              !documents.some((doc) => doc.referenceId === selected.referenceId)
          )
        );
      }
    };

    const handleDocumentChange =
      (doc: DocumentSlice) => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
          onChange([...value, doc]);
        } else {
          onChange(value.filter((selected) => selected.referenceId !== doc.referenceId));
        }
      };

    return (
      <div className={classes.root} ref={ref}>
        {label && <Typography variant="h6">{label}</Typography>}
        <FormGroup key={groupingKey}>
          <FormControlLabel
            control={
              <Checkbox
                name={groupingKey}
                checked={isAllChecked}
                onChange={handleGroupChange}
              />
            }
            label={
              <Box className={classes.iconWithText}>
                <FolderIcon />
                <Typography variant="h6" style={{ paddingTop: "2px" }}>
                  {groupingKey}
                </Typography>
              </Box>
            }
          />
          <Box className={classes.referenceRoot}>
            {documents.map((document) => {
              const isChecked = value.some(
                (selected) => selected.referenceId === document.referenceId
              );

              return (
                <Box className={classes.referenceRow} key={document.referenceId}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checkedIcon={<CheckedIcon htmlColor={Color.PrimaryLight} />}
                        name={document.name ?? undefined}
                        checked={isChecked}
                        onChange={handleDocumentChange(document)}
                      />
                    }
                    label={
                      <Box className={classes.iconWithText}>
                        <DraftIcon htmlColor={Color.DetailsInput} />
                        <Typography variant="body1" style={{ paddingTop: "2px" }}>
                          {document.name}
                        </Typography>
                      </Box>
                    }
                  />
                  <OpenInNewIcon
                    className={classes.previewIcon}
                    fontSize={"small"}
                    htmlColor={Color.DetailsInput}
                    onClick={() => window.open(document.document.PreviewURL)}
                  />
                </Box>
              );
            })}
          </Box>
        </FormGroup>
      </div>
    );
  }
);

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    paddingLeft: "0.5rem"
  },
  referenceRoot: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    paddingLeft: "1rem"
  },
  referenceRow: {
    display: "flex",
    alignItems: "center"
  },
  iconWithText: {
    display: "flex",
    flexDirection: "row",
    gap: "0.25rem",
    alignItems: "center",
    paddingLeft: "0.25rem"
  },
  previewIcon: {
    cursor: "pointer"
  }
}));
